import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Heart, Trash2, Search, X, Check } from "lucide-react";
import { blClient, adminClient } from "./client";

const FIELDS = [
  ["variety_name", "Variety name *", "e.g. Karma Choc", true],
  ["foliage_colour", "Foliage colour", "burgundy-black, near-black"],
  ["bloom_colour", "Bloom colour", "scarlet, plum, lemon"],
  ["breeder", "Breeder / source"],
  ["location", "Where you're growing it"],
  ["contributor", "Your name (for credit)"],
];

async function filesToDataUrls(files) {
  return Promise.all(files.map((f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f);
  })));
}

export function BLCatalog({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("alpha");
  const [adding, setAdding] = useState(false);
  const [showPending, setShowPending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (showPending && isAdmin) {
        const r = await adminClient.get("/blackleaf/admin/entries/pending");
        setItems(r.data.items);
      } else {
        const r = await blClient.get("/blackleaf/entries", { params: { sort } });
        setItems(r.data.items);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, showPending]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => [i.variety_name, i.foliage_colour, i.breeder, i.contributor]
      .some((s) => (s || "").toLowerCase().includes(q)));
  }, [items, search]);

  const heart = async (id) => {
    const r = await blClient.post(`/blackleaf/entries/${id}/heart`);
    setItems((prev) => prev.map((i) => i.id === id ? r.data : i));
  };
  const removeEntry = async (id) => {
    if (!window.confirm("Remove this variety from the catalogue?")) return;
    if (isAdmin) await adminClient.delete(`/blackleaf/admin/entries/${id}`);
    else await blClient.delete(`/blackleaf/entries/${id}`);
    toast.success("Removed"); load();
  };
  const approve = async (id) => {
    await adminClient.post(`/blackleaf/admin/entries/${id}/approve`);
    toast.success("Approved"); load();
  };

  return (
    <section className="bl-section" data-testid="bl-catalog">
      <header className="bl-section-head">
        <div>
          <p className="bl-kicker">Catalogue</p>
          <h2 className="bl-h2">Black Leaf Dahlia varieties</h2>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button onClick={() => setShowPending((v) => !v)} className="bl-btn-ghost" data-testid="bl-catalog-pending-toggle">
              {showPending ? "Approved" : "Pending"}
            </button>
          )}
          <button onClick={() => setAdding(true)} className="bl-btn" data-testid="bl-catalog-add"><Plus size={14}/> Log a variety</button>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-bl-soft" />
          <input className="bl-field pl-9" placeholder="Search variety, foliage, breeder…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bl-catalog-search"/>
        </div>
        <select className="bl-field sm:w-56" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="bl-catalog-sort">
          <option value="alpha">Alphabetical</option>
          <option value="hearts">Most loved</option>
          <option value="newest">Newest first</option>
        </select>
      </div>

      {loading ? <Loader2 size={20} className="animate-spin mx-auto block text-bl-soft my-10"/> :
        filtered.length === 0 ? <div className="bl-card p-10 text-center text-bl-soft italic">
          {showPending ? "No pending entries — all caught up." : "Catalogue is bare — be the first to log one."}
        </div> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((i) => (
            <article key={i.id} className="bl-card overflow-hidden flex flex-col" data-testid={`bl-entry-${i.id}`}>
              {(i.photos || [])[0] && <div className="aspect-[4/3]"><img src={i.photos[0]} alt={i.variety_name} className="w-full h-full object-cover" loading="lazy"/></div>}
              <div className="p-4 flex flex-col flex-1">
                <h3 className="bl-h3">{i.variety_name}</h3>
                {i.foliage_colour && <p className="text-xs italic text-bl-soft mt-0.5">Foliage: {i.foliage_colour}</p>}
                {(i.bloom_colour || i.breeder) && <p className="text-xs text-bl-soft mt-0.5">{[i.bloom_colour, i.breeder].filter(Boolean).join(" · ")}</p>}
                {i.notes && <p className="text-sm mt-2 line-clamp-3">{i.notes}</p>}
                {i.status === "pending" && <span className="bl-badge mt-2">Pending</span>}
                <div className="flex items-center justify-between mt-auto pt-3 gap-2">
                  <button onClick={() => heart(i.id)} className="inline-flex items-center gap-1 text-sm text-bl-rose hover:scale-110 transition-transform" data-testid={`bl-heart-${i.id}`}>
                    <Heart size={14} fill="currentColor"/> {i.hearts || 0}
                  </button>
                  <div className="text-xs text-bl-soft italic flex-1 truncate">{i.contributor ? `— ${i.contributor}` : ""}</div>
                  {isAdmin && i.status === "pending" && (
                    <button onClick={() => approve(i.id)} className="bl-btn-ghost" data-testid={`bl-approve-${i.id}`}><Check size={12}/></button>
                  )}
                  <button onClick={() => removeEntry(i.id)} className="bl-btn-ghost" data-testid={`bl-del-${i.id}`}><Trash2 size={12}/></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      }

      {adding && <AddDrawer onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); }} />}
    </section>
  );
}

function AddDrawer({ onClose, onSaved }) {
  const [form, setForm] = useState({ variety_name: "", foliage_colour: "", bloom_colour: "", breeder: "", location: "", contributor: "", notes: "", photos: [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const list = await filesToDataUrls(files);
    set("photos", [...form.photos, ...list]);
    e.target.value = "";
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.variety_name.trim()) { toast.error("Variety name required"); return; }
    setBusy(true);
    try {
      await blClient.post("/blackleaf/entries", form);
      toast.success("Submitted — awaiting admin approval");
      onSaved();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40" data-testid="bl-catalog-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-bl-paper overflow-y-auto shadow-2xl black-leaf-theme">
        <div className="sticky top-0 bg-bl-paper border-b border-bl-line flex items-center justify-between px-5 py-4">
          <h2 className="bl-h2">Log a variety</h2>
          <button onClick={onClose} className="bl-btn-ghost"><X size={12}/></button>
        </div>
        <form onSubmit={submit} className="px-5 py-5 space-y-3">
          {FIELDS.map(([k, label, placeholder, req]) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">{label}</span>
              <input className="bl-field mt-1.5" value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} required={!!req} data-testid={`bl-field-${k}`}/>
            </label>
          ))}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">Notes</span>
            <textarea className="bl-field mt-1.5" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}/>
          </label>
          <div>
            <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">Photos</span>
            <div className="mt-1.5 flex flex-wrap gap-2 items-center">
              {form.photos.map((u, i) => (
                <div key={i} className="relative w-16 h-16 rounded overflow-hidden border border-bl-line">
                  <img src={u} alt="" className="w-full h-full object-cover"/>
                  <button type="button" onClick={() => set("photos", form.photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-bl-rose text-white text-xs w-5 h-5">×</button>
                </div>
              ))}
              <label className="bl-btn-ghost cursor-pointer">+ Photo<input type="file" accept="image/*" multiple className="hidden" onChange={pickPhoto}/></label>
            </div>
          </div>
          <button type="submit" className="bl-btn w-full mt-3" disabled={busy} data-testid="bl-catalog-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
            Submit for approval
          </button>
        </form>
      </aside>
    </div>
  );
}
